import type { TypedComponentRendering } from '@core/jss-models';
import { getDroplinkValue } from '@core/jss-utils';
import { OfferSaverContent } from '@innogy/offer-saver';
import { getFieldValue } from '@sitecore-jss/sitecore-jss-angular';

import type {
  CommodityOfferAlignment,
  CommodityOfferComponentLabels,
  CommodityOfferConfig,
  CommodityOfferContent,
  CommodityOfferLabels,
  ScCommodityOffer,
} from './commodity-offers.model';

export function scToCommodityOfferContentMapper(
  rendering: TypedComponentRendering<ScCommodityOffer>
): CommodityOfferContent {
  const commodityOfferConfig: CommodityOfferConfig = {
    variant: getDroplinkValue<CommodityOfferAlignment>(
      rendering.fields,
      'Variant'
    ),
    useMinimalOfferCards: getFieldValue(
      rendering,
      'UseMinimalOfferCards',
      false
    ),
    hideCommodityProductBreakdownTable: getFieldValue(
      rendering,
      'HideCommodityProductBreakdownTable',
      false
    ),
    useAlternateCommodityTariffView: getFieldValue(
      rendering,
      'UseAlternateCommodityTariffView',
      false
    ),
    offerSaverShown: getFieldValue(rendering, 'OfferSaverShown', false),
    showMyContract: getFieldValue(rendering, 'ShowMyContract', false),
    enableNewOfferCardTitles: getFieldValue(
      rendering,
      'EnableNewOfferCardTitles',
      false
    ),
    enableRemoveBBA: getFieldValue(rendering, 'EnableRemoveBBA', false),
    showRetentionQuestionnaire: getFieldValue(
      rendering,
      'ShowRetentionQuestionnaire',
      false
    ),
    questionnaireUrl: getFieldValue(rendering, 'QuestionnaireURL', {
      href: '',
    }),
    showFootnoteAlwaysBelow: getFieldValue(
      rendering,
      'ShowFootnoteAlwaysBelow',
      false
    ),
    enableFilterByContractType: getFieldValue(
      rendering,
      'EnableFilterByContractType',
      false
    ),
    centerAlignedTariffLink: getFieldValue(
      rendering,
      'CenterAlignedTariffLink',
      false
    ),
    enableHelpChoosingMyContract: getFieldValue(
      rendering,
      'EnableHelpChoosingMyContract',
      false
    ),
    termijnbedragVariantA: getFieldValue(
      rendering,
      'TermijnbedragVariantA',
      false
    ),
    termijnbedragVariantB: getFieldValue(
      rendering,
      'TermijnbedragVariantB',
      false
    ),
    redeliveryNoticeSwitchbackTest: getFieldValue(
      rendering,
      'redeliveryNoticeSwitchbackTest',
      false
    ),
  };

  const commodityOfferComponentLabels: CommodityOfferComponentLabels = {
    componentTitle: getFieldValue(rendering, 'ComponentTitle'),
    calculationBasedOnLabel: getFieldValue(
      rendering,
      'CalculationBasedOnLabel'
    ),
    adjustCalculationLabel: getFieldValue(rendering, 'AdjustCalculationLabel'),
    offerFootNote: getFieldValue(rendering, 'OfferFootNote'),
    componentFootNote: getFieldValue(rendering, 'ComponentFootNote'),
    technicalErrorTitle: getFieldValue(rendering, 'TechnicalErrorTitle'),
    technicalErrorMessage: getFieldValue(rendering, 'TechnicalErrorMessage'),
    notEligibleErrorTitle: getFieldValue(rendering, 'NotEligibleErrorTitle'),
    notEligibleErrorMessage: getFieldValue(
      rendering,
      'NotEligibleErrorMessage'
    ),
    segmentedContractLabel: getFieldValue(rendering, 'SegmentedContractLabel'),
    segmentedControlLabel: getFieldValue(rendering, 'SegmentedControlLabel'),
    segmentFixedLabel: getFieldValue(rendering, 'SegmentFixedLabel'),
    segmentFlexLabel: getFieldValue(rendering, 'SegmentFlexLabel'),
    segmentDynamicLabel: getFieldValue(rendering, 'SegmentDynamicLabel'),
    customerIdLabel: getFieldValue(rendering, 'CustomerIDLabel'),
    referenceIdLabel: getFieldValue(rendering, 'ReferenceIDLabel'),
    redeliveryNotice: getFieldValue(rendering, 'RedeliveryNotice')
      ? rendering.fields.RedeliveryNotice
      : undefined,
    thuisvoordeelAddonTitle: getFieldValue(
      rendering,
      'thuisvoordeelAddonTitle',
      ''
    ),
    thuisvoordeelAddonText: getFieldValue(
      rendering,
      'thuisvoordeelAddonText',
      ''
    ),
    thuisvoordeelAddonImage: getFieldValue(
      rendering,
      'thuisvoordeelAddonImage'
    ),
  };

  const commodityOfferLabels: CommodityOfferLabels = {
    priceDurationLabel: getFieldValue(rendering, 'PriceDurationLabel'),
    priceDurationLabelSuffix: getFieldValue(
      rendering,
      'PriceDurationLabelSuffix'
    ),
    priceDurationAlternateLabel: getFieldValue(
      rendering,
      'PriceDurationAlternateLabel'
    ),
    withDiscountAlternateLabel: getFieldValue(
      rendering,
      'WithDiscountAlternateLabel'
    ),
    electricityLabel: getFieldValue(rendering, 'ElectricityLabel'),
    gasLabel: getFieldValue(rendering, 'GasLabel'),
    cashbackLabel: getFieldValue(rendering, 'CashbackLabel'),
    yearlyCostsTotalDynamicWithIncentiveLabel: getFieldValue(
      rendering,
      'YearlyCostsTotalDynamicWithIncentiveLabel'
    ),
    yearlyCostsTotalDynamicWithoutIncentiveLabel: getFieldValue(
      rendering,
      'YearlyCostsTotalDynamicWithoutIncentiveLabel'
    ),
    yearlyCostsTotalWithIncentiveLabel: getFieldValue(
      rendering,
      'YearlyCostsTotalWithIncentiveLabel'
    ),
    yearlyCostsTotalWithoutIncentiveLabel: getFieldValue(
      rendering,
      'YearlyCostsTotalWithoutIncentiveLabel'
    ),
    yearlyCostsTotalMultiYearWithIncentiveLabel: getFieldValue(
      rendering,
      'YearlyCostsTotalMultiYearWithIncentiveLabel'
    ),
    yearlyCostsTotalMultiYearWithoutIncentiveLabel: getFieldValue(
      rendering,
      'YearlyCostsTotalMultiYearWithoutIncentiveLabel'
    ),
    tariffModalLinkTextLabel: getFieldValue(
      rendering,
      'TariffModalLinkTextLabel'
    ),
    tariffModalDescriptionLabel: getFieldValue(
      rendering,
      'TariffModalDescriptionLabel'
    ),
    moreInfoPriceAndConditionLinkLabel: getFieldValue(
      rendering,
      'MoreInfoPriceAndConditionLinkLabel'
    ),
    offerSubmitButtonLabel: getFieldValue(rendering, 'OfferSubmitButtonLabel'),
    offerSubmitDescriptionLabel: getFieldValue(
      rendering,
      'OfferSubmitDescriptionLabel'
    ),
    offerSubmitDescriptionTooltip: getFieldValue(
      rendering,
      'OfferSubmitDescriptionTooltip'
    ),
    memberGetMemberSharedValue: getFieldValue(
      rendering,
      'MemberGetMemberSharedValue'
    ),
    costsPerYearLabel: getFieldValue(rendering, 'CostsPerYearLabel'),
    costsPerMonthLabel: getFieldValue(rendering, 'CostsPerMonthLabel'),
    budgetbillAmountLabel: getFieldValue(rendering, 'BudgetbillAmountLabel'),
    budgetbillAmountDynamicLabel: getFieldValue(
      rendering,
      'BudgetbillAmountDynamicLabel'
    ),
    withoutDiscountLabel: getFieldValue(rendering, 'WithoutDiscountLabel'),
    withoutDiscountTooltip: getFieldValue(rendering, 'WithoutDiscountTooltip'),
    costsPerYearWithDiscountLabel: getFieldValue(
      rendering,
      'CostsPerYearWithDiscountLabel'
    ),
    costsPerMonthWithDiscountLabel: getFieldValue(
      rendering,
      'CostsPerMonthWithDiscountLabel'
    ),
    showBudgetbillAmount: getFieldValue<boolean>(
      rendering,
      'ShowBudgetbillAmount',
      false
    ),
    showDiscountAmountInRowFormat: getFieldValue<boolean>(
      rendering,
      'ShowDiscountAmountInRowFormat',
      false
    ),
    monthlyCostsLabel: getFieldValue(rendering, 'MonthlyCostsLabel'),
    monthlyCostsAdditionalLabel: getFieldValue(
      rendering,
      'MonthlyCostsAdditionalLabel'
    ),
    monthlyCostsDynamicLabel: getFieldValue(
      rendering,
      'MonthlyCostsDynamicLabel'
    ),
    monthlyCostsLabelSuffix: getFieldValue(
      rendering,
      'MonthlyCostsLabelSuffix'
    ),
    monthlyCostsLabelDiscountInfix: getFieldValue(
      rendering,
      'MonthlyCostsLabelDiscountInfix'
    ),
    proposedBudgetBillBefore: getFieldValue(
      rendering,
      'ProposedBudgetBillBefore'
    ),
    proposedBudgetBillBeforeForDynamic: getFieldValue(
      rendering,
      'ProposedBudgetBillBeforeForDynamic'
    ),
    proposedBudgetBillBeforeTooltip: getFieldValue(
      rendering,
      'ProposedBudgetbillBeforeTooltip'
    ),
    proposedBudgetBillBeforeTooltipForDynamic: getFieldValue(
      rendering,
      'ProposedBudgetbillBeforeTooltipForDynamic'
    ),
    expectedYearlyCostLabel: getFieldValue(
      rendering,
      'ExpectedYearlyCostLabel'
    ),
    dropdownElectricityTitle: getFieldValue(
      rendering,
      'DropdownElectricityTitle'
    ),
    dropdownGasTitle: getFieldValue(rendering, 'DropdownGasTitle'),
    dropdownFixedDeliveryCostsLabel: getFieldValue(
      rendering,
      'DropdownFixedDeliveryCostsLabel'
    ),
    dropdownRedeliveryTitle: getFieldValue(
      rendering,
      'DropdownRedeliveryTitle'
    ),
    dropdownFixedRedeliveryCostsLabel: getFieldValue(
      rendering,
      'DropdownFixedRedeliveryCostsLabel'
    ),
    dropdownDynamicPricingExpectedDeliveryRateTitle: getFieldValue(
      rendering,
      'DropdownDynamicPricingExpectedDeliveryRateTitle'
    ),
    currentContractTitle: getFieldValue(rendering, 'CurrentContractTitle'),
    currentContractBodyFixed: getFieldValue(
      rendering,
      'CurrentContractBodyFixed'
    ),
    currentContractBodyVariable: getFieldValue(
      rendering,
      'CurrentContractBodyVariable'
    ),
    contractAdviceHeader: getFieldValue(rendering, 'ContractAdviceHeader'),
    contractAdviceBody: getFieldValue(rendering, 'ContractAdviceBody'),
    contractAdviceButtonLabel: getFieldValue(
      rendering,
      'ContractAdviceButtonLabel'
    ),
    contractAdviceModalHeader: getFieldValue(
      rendering,
      'ContractAdviceModalHeader'
    ),
    contractAdviceModalContent: getFieldValue(
      rendering,
      'ContractAdviceModalContent'
    ),
    promoCodeLabel: getFieldValue(rendering, 'PromoCodeLabel'),
    questionnaireBestChoiceTitle: getFieldValue(
      rendering,
      'questionnaireBestChoiceTitle'
    ),
    questionnaireAlternateChoiceTitle: getFieldValue(
      rendering,
      'questionnaireAlternateChoiceTitle'
    ),
    questionnaireVariableToSuccesUrl: getFieldValue(
      rendering,
      'questionnaireVariableToSuccesUrl'
    ),
    questionnaireVariableCardTitle: getFieldValue(
      rendering,
      'questionnaireVariableCardTitle'
    ),
    questionnaireVariableCardDescription: getFieldValue(
      rendering,
      'questionnaireVariableCardDescription'
    ),
    questionnaireExplanationButtonTitle: getFieldValue(
      rendering,
      'questionnaireExplanationButtonTitle'
    ),
    questionnaireVariableCardSubmitTitle: getFieldValue(
      rendering,
      'questionnaireVariableCardSubmitTitle'
    ),
    questionnaireDynamicCardDescription: getFieldValue(
      rendering,
      'questionnaireDynamicCardDescription'
    ),
    questionnaire1YearCardDescription: getFieldValue(
      rendering,
      'questionnaire1YearCardDescription'
    ),
    questionnaire3YearsCardDescription: getFieldValue(
      rendering,
      'questionnaire3YearsCardDescription'
    ),
    questionnaireAdviceModalTitle: getFieldValue(
      rendering,
      'questionnaireAdviceModalTitle'
    ),
    questionnaireVariableModalTitle: getFieldValue(
      rendering,
      'questionnaireVariableModalTitle'
    ),
    questionnaireVariableModalDescription: getFieldValue(
      rendering,
      'questionnaireVariableModalDescription'
    ),
    questionnaireVariableModalSubmitTitle: getFieldValue(
      rendering,
      'questionnaireVariableModalSubmitTitle'
    ),
    questionnaireVariableModalCancelTitle: getFieldValue(
      rendering,
      'questionnaireVariableModalCancelTitle'
    ),
    costEstimationLabel: getFieldValue(rendering, 'costEstimationLabel', ''),
  };

  return {
    ...commodityOfferConfig,
    ...commodityOfferComponentLabels,
    ...commodityOfferLabels,
    offerSaver: new OfferSaverContent(rendering),
  };
}
